import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import FileIntentBridge from "./components/FileIntentBridge";
import InstallDesktopBanner from "./components/InstallDesktopBanner";
import { isMobileApp } from "./lib/platform";

import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import CsvEditor from "./pages/CsvEditor";
import MyFiles from "./pages/MyFiles";
import PaymentSuccess from "./pages/PaymentSuccess";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import AdminInbox from "./pages/AdminInbox";
import AdminLeads from "./pages/AdminLeads";
import AdminQuestions from "./pages/AdminQuestions";
import AskHub from "./pages/AskHub";
import AskDetail from "./pages/AskDetail";
import SharedView from "./pages/SharedView";
import Faq from "./pages/Faq";
import Blog from "./pages/Blog";
import CsvViewer from "./pages/seo/CsvViewer";
import CsvEditorPage from "./pages/seo/CsvEditor";
import OpenCsvFiles from "./pages/seo/OpenCsvFiles";
import WhatIsCsvFile from "./pages/seo/WhatIsCsvFile";
import HowToEditCsvFiles from "./pages/seo/HowToEditCsvFiles";
import CsvVsExcel from "./pages/seo/CsvVsExcel";
import LightweightCsvEditor from "./pages/seo/LightweightCsvEditor";
import PrivacyFocusedCsvViewer from "./pages/seo/PrivacyFocusedCsvViewer";
import FastCsvViewer from "./pages/seo/FastCsvViewer";
import CsvUseCases from "./pages/seo/CsvUseCases";
import ImportExportCsv from "./pages/seo/ImportExportCsv";
import Features from "./pages/seo/Features";
import SaveCsvForPreset from "./pages/seo/SaveCsvForPreset";
import HowCsvFilesWork from "./pages/blog/HowCsvFilesWork";
import BestCsvEditor from "./pages/blog/BestCsvEditor";
import OpenLargeCsvFiles from "./pages/blog/OpenLargeCsvFiles";
import WhyCsvStillMatters from "./pages/blog/WhyCsvStillMatters";
import MobileUnlock from "./pages/MobileUnlock";
import { useAuth } from "./context/AuthContext";
import { Navigate } from "react-router-dom";

function Shell({ children }) {
    return (
        <div className="App flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-1">{children}</main>
            <Footer />
            <InstallDesktopBanner />
        </div>
    );
}

// Minimal shell for the Android app — no marketing footer or full nav.
function MobileShell({ children }) {
    return (
        <div className="App flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-1">{children}</main>
        </div>
    );
}

// Routes the home path inside the Android shell based on auth + paid state.
function MobileHomeRedirect() {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-loading">
                <div className="ui-heading text-sm text-ink-soft">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.is_paid || user.role === "admin") return <Navigate to="/app" replace />;
    return <Navigate to="/unlock" replace />;
}

function MobileRouter() {
    return (
        <>
            <ScrollToTop />
            <FileIntentBridge />
            <Routes>
                <Route path="/" element={<MobileHomeRedirect />} />
                <Route path="/login" element={<MobileShell><Login /></MobileShell>} />
                <Route path="/signup" element={<MobileShell><Signup /></MobileShell>} />
                <Route path="/forgot-password" element={<MobileShell><ForgotPassword /></MobileShell>} />
                <Route path="/reset-password" element={<MobileShell><ResetPassword /></MobileShell>} />
                <Route path="/unlock" element={<MobileShell><ProtectedRoute><MobileUnlock /></ProtectedRoute></MobileShell>} />
                <Route path="/account" element={<MobileShell><ProtectedRoute><Account /></ProtectedRoute></MobileShell>} />
                <Route path="/app" element={<MobileShell><ProtectedRoute requirePaidMobile><CsvEditor /></ProtectedRoute></MobileShell>} />
                <Route path="/files" element={<MobileShell><ProtectedRoute requirePaidMobile><MyFiles /></ProtectedRoute></MobileShell>} />
                <Route path="/payment-success" element={<MobileShell><ProtectedRoute><PaymentSuccess /></ProtectedRoute></MobileShell>} />
                <Route path="/admin" element={<MobileShell><ProtectedRoute adminOnly><Admin /></ProtectedRoute></MobileShell>} />
                <Route path="/s/:token" element={<SharedView />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}

function AppRouter() {
    const location = useLocation();
    // Detect Emergent OAuth callback synchronously during render
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    // Inside the Android shell, use the slimmed-down router (no marketing,
    // unpaid users get the IAP unlock screen instead of Stripe redirect).
    if (isMobileApp()) {
        return <MobileRouter />;
    }
    return (
        <>
            <ScrollToTop />
            <FileIntentBridge />
            <Routes>
            <Route path="/" element={<Shell><Landing /></Shell>} />
            <Route path="/pricing" element={<Shell><Pricing /></Shell>} />
            <Route path="/about" element={<Shell><About /></Shell>} />
            <Route path="/terms" element={<Shell><Terms /></Shell>} />
            <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
            <Route path="/login" element={<Shell><Login /></Shell>} />
            <Route path="/signup" element={<Shell><Signup /></Shell>} />
            <Route path="/forgot-password" element={<Shell><ForgotPassword /></Shell>} />
            <Route path="/reset-password" element={<Shell><ResetPassword /></Shell>} />
            <Route path="/payment-success" element={<Shell><ProtectedRoute><PaymentSuccess /></ProtectedRoute></Shell>} />
            <Route path="/account" element={<Shell><ProtectedRoute><Account /></ProtectedRoute></Shell>} />
            <Route path="/files" element={<Shell><ProtectedRoute requirePaid><MyFiles /></ProtectedRoute></Shell>} />
            <Route path="/app" element={<Shell><CsvEditor /></Shell>} />
            <Route path="/admin" element={<Shell><ProtectedRoute adminOnly><Admin /></ProtectedRoute></Shell>} />
            <Route path="/admin/inbox" element={<Shell><ProtectedRoute adminOnly><AdminInbox /></ProtectedRoute></Shell>} />
            <Route path="/admin/leads" element={<Shell><ProtectedRoute adminOnly><AdminLeads /></ProtectedRoute></Shell>} />
            <Route path="/admin/questions" element={<Shell><ProtectedRoute adminOnly><AdminQuestions /></ProtectedRoute></Shell>} />
            <Route path="/ask" element={<Shell><AskHub /></Shell>} />
            <Route path="/ask/:slug" element={<Shell><AskDetail /></Shell>} />

            {/* SEO landing pages */}
            <Route path="/csv-viewer" element={<Shell><CsvViewer /></Shell>} />
            <Route path="/csv-editor" element={<Shell><CsvEditorPage /></Shell>} />
            <Route path="/open-csv-files" element={<Shell><OpenCsvFiles /></Shell>} />
            <Route path="/what-is-a-csv-file" element={<Shell><WhatIsCsvFile /></Shell>} />
            <Route path="/how-to-edit-csv-files" element={<Shell><HowToEditCsvFiles /></Shell>} />
            <Route path="/csv-vs-excel" element={<Shell><CsvVsExcel /></Shell>} />
            <Route path="/lightweight-csv-editor" element={<Shell><LightweightCsvEditor /></Shell>} />
            <Route path="/privacy-focused-csv-viewer" element={<Shell><PrivacyFocusedCsvViewer /></Shell>} />
            <Route path="/fast-csv-viewer" element={<Shell><FastCsvViewer /></Shell>} />
            <Route path="/csv-use-cases" element={<Shell><CsvUseCases /></Shell>} />
            <Route path="/import-export-csv" element={<Shell><ImportExportCsv /></Shell>} />
            <Route path="/features" element={<Shell><Features /></Shell>} />
            <Route path="/save-csv-for/:preset" element={<Shell><SaveCsvForPreset /></Shell>} />

            {/* Resources */}
            <Route path="/faq" element={<Shell><Faq /></Shell>} />
            <Route path="/blog" element={<Shell><Blog /></Shell>} />
            <Route path="/blog/how-csv-files-work" element={<Shell><HowCsvFilesWork /></Shell>} />
            <Route path="/blog/best-csv-editor" element={<Shell><BestCsvEditor /></Shell>} />
            <Route path="/blog/open-large-csv-files" element={<Shell><OpenLargeCsvFiles /></Shell>} />
            <Route path="/blog/why-csv-still-matters" element={<Shell><WhyCsvStillMatters /></Shell>} />

            <Route path="/s/:token" element={<SharedView />} />
            <Route path="*" element={<Shell><Landing /></Shell>} />
        </Routes>
        </>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <Toaster position="top-center" richColors />
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}
